
import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { formatCurrency } from "./utils";

interface ForecastAccuracyChartProps {
  data: any[];
  getFilterLabel: () => string;
}

export const ForecastAccuracyChart: React.FC<ForecastAccuracyChartProps> = ({ data, getFilterLabel }) => {
  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, value } = props;
    if (value === "N/A") return null;
    return (
      <text 
        x={x + width / 2} 
        y={y - 10} 
        fill="#33C3F0" 
        fontWeight="bold" 
        textAnchor="middle" 
        fontSize="12"
      >
        {value}
      </text>
    );
  };

  return (
    <div className="bg-secondary/80 p-4 rounded-lg border border-primary/20">
      <h3 className="text-primary-light font-medium mb-4">
        Forecast Accuracy Trend
        <span className="text-purple-300 ml-1">{getFilterLabel()}</span>
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart 
          data={data} 
          margin={{ top: 20, right: 30, left: 20, bottom: 15 }}
        >
          <defs>
            <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.9} />
              <stop offset="95%" stopColor="#4F46E5" stopOpacity={0.8} />
            </linearGradient>
            <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1EAEDB" stopOpacity={0.9} />
              <stop offset="95%" stopColor="#33C3F0" stopOpacity={0.8} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#555" opacity={0.4} />
          <XAxis 
            dataKey="year" 
            stroke="#9b87f5" 
            tick={{ fill: '#9b87f5', fontSize: 11 }} 
            axisLine={{ stroke: '#444' }} 
          />
          <YAxis 
            stroke="#9b87f5" 
            tickFormatter={value => formatCurrency(value)} 
            tick={{ fill: '#9b87f5', fontSize: 11 }} 
            axisLine={{ stroke: '#444' }} 
          />
          <Tooltip 
            formatter={(value: number, name: string) => {
              const formattedValue = formatCurrency(value);
              if (name === 'forecast') {
                return [`Forecast: ${formattedValue}`, ''];
              } else if (name === 'actual') {
                return [`Actuals: ${formattedValue}`, ''];
              }
              return [formattedValue, name];
            }} 
            contentStyle={{
              backgroundColor: '#1A1F2C',
              borderColor: '#9b87f5',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
              borderRadius: '8px',
              color: '#fff'
            }} 
            labelStyle={{
              color: '#D6BCFA',
              fontWeight: 'bold',
              marginBottom: '5px'
            }} 
            cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }} 
          />
          <Legend 
            wrapperStyle={{
              color: '#fff',
              fontSize: 12,
              paddingTop: '10px'
            }} 
            iconType="circle" 
            iconSize={10} 
          />
          <Bar 
            name="Forecast" 
            dataKey="forecast" 
            fill="url(#forecastGradient)" 
            radius={[5, 5, 0, 0]} 
            barSize={30}
          >
            <LabelList dataKey="accuracy" position="top" content={renderCustomizedLabel} />
          </Bar>
          <Bar 
            name="Actual" 
            dataKey="actual" 
            fill="url(#actualGradient)" 
            radius={[5, 5, 0, 0]} 
            barSize={30} 
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
